import { HTMLAttributes } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledInputSelect from 'typescript/components/forms/controlled_input_select';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { administrativeDistrictCodeTransformers } from 'typescript/utilities/address_utils';

export const HIDDEN_INPUT_TEST_ID = 'hidden-input-for-autofill-test-id';

interface AdministrativeDistrictLevel1Props
  extends HTMLAttributes<HTMLElement> {
  name?: string;
  label?: string;
  options: Record<string, string>;
  disabled?: boolean;
}

const AdministrativeDistrictLevel1Select = ({
  className,
  name,
  label,
  options,
  disabled = false,
}: AdministrativeDistrictLevel1Props) => {
  const { t, i18n } = useTranslation();
  const { control, setValue } = useFormContext();
  const { countryCode } = useLocaleInfo();

  // because market select does not support autocomplete, we need to add a hidden input
  // to capture the value and set it in the form when browser autofills the form
  const onHiddenInputChange = (event: { target: HTMLInputElement }) => {
    const target = event.target as HTMLInputElement;
    const value = target.value;

    // the value could be the code or the name, but we need the code to set the value in the form
    const code = administrativeDistrictCodeTransformers[countryCode]({
      userValue: value,
      options,
    });

    // if the value is not a valid code / undefined, do not set it in the form
    if (!code) {
      return;
    }

    setValue(name, code, { shouldValidate: true });
  };

  return (
    <>
      <input
        data-testid={HIDDEN_INPUT_TEST_ID}
        type="text"
        autoComplete="address-level1"
        style={{ height: 0, width: 0, opacity: 0, position: 'absolute' }}
        onChange={onHiddenInputChange}
      />
      <ControlledInputSelect
        className={className}
        control={control}
        name={name}
        label={label}
        options={Object.entries(options).map(([value, label]) => ({
          value,
          displayValue: i18n.exists(label) ? t(label) : label,
        }))}
        shouldMaskValues
        disabled={disabled}
      />
    </>
  );
};

export default AdministrativeDistrictLevel1Select;
